<template>
    <b-modal
        id="access-control-user-form-modal"
        v-model="visibleModel"
        :title="mode === 'create' ? '사용자 추가' : '사용자 수정'"
        size="md"
        hide-footer
        centered
    >
        <div class="modal-form-compact">
            <div v-if="mode === 'create'" class="form-group-compact user-search-section">
                <label>메인 시스템 사용자 검색</label>
                <div class="user-search-input-wrapper">
                    <CarbonInput
                        v-model="userSearchQuery"
                        placeholder="사용자 ID 또는 이름 2자 이상 입력"
                        @input="debouncedSearchUser"
                    />
                    <i v-if="userSearchLoading" class="fas fa-spinner fa-spin user-search-spinner"></i>
                    <div v-if="userSearchResults.length > 0" class="user-search-dropdown">
                        <button
                            v-for="u in userSearchResults"
                            :key="u.loginId"
                            type="button"
                            class="user-search-item"
                            @click="selectSearchUser(u)"
                        >
                            <span class="user-search-id">{{ u.loginId }}</span>
                            <span class="user-search-name">{{ u.userName || "-" }}</span>
                            <span v-if="u.carbonMapping?.isMapped" class="user-search-badge mapped">맵핑됨</span>
                        </button>
                    </div>
                </div>
                <small class="text-muted">메인 시스템에 등록된 사용자만 Carbon에 추가할 수 있습니다.</small>
            </div>
            <div class="form-group-compact">
                <label>사용자 ID *</label>
                <CarbonInput v-model="localForm.loginId" :disabled="mode === 'edit'" />
            </div>
            <div class="form-group-compact">
                <label>이름 *</label>
                <CarbonInput v-model="localForm.userName" />
            </div>
            <div class="form-group-compact">
                <label>이메일 *</label>
                <CarbonInput v-model="localForm.email" type="email" />
            </div>
            <div class="form-group-compact">
                <label>부서</label>
                <CarbonInput v-model="localForm.department" />
            </div>
            <div class="form-group-compact">
                <label>상태</label>
                <CarbonSelect v-model="localForm.status" :options="statusOptions" />
            </div>
        </div>
        <div class="modal-footer-compact">
            <CarbonButton @click="$emit('input', false)">취소</CarbonButton>
            <CarbonButton variant="primary" @click="handleSave">저장</CarbonButton>
        </div>
    </b-modal>
</template>

<script>
import carbonApi from "@/submodules/carbon-plugin/api";
import CarbonCodes from "../../../codes";
import accessControlMixin from "../mixins/accessControlMixin";
import CarbonButton from "../../common/CarbonButton.vue";
import CarbonInput from "../../common/CarbonInput.vue";
import CarbonSelect from "../../common/CarbonSelect.vue";

export default {
    name: "UserFormModal",
    components: { CarbonButton, CarbonInput, CarbonSelect },
    mixins: [accessControlMixin],
    props: {
        value: { type: Boolean, default: false },
        mode: { type: String, default: "create" },
        form: {
            type: Object,
            default: () => ({
                loginId: "",
                userName: "",
                email: "",
                department: "",
                status: "ACTIVE",
            }),
        },
    },
    data() {
        return {
            localForm: {
                loginId: "",
                userName: "",
                email: "",
                department: "",
                status: "ACTIVE",
            },
            userSearchQuery: "",
            userSearchResults: [],
            userSearchLoading: false,
            userSearchDebounceTimer: null,
        };
    },
    watch: {
        value(visible) {
            if (visible) {
                this.localForm = { ...this.form };
                this.userSearchQuery = "";
                this.userSearchResults = [];
            }
        },
        form: {
            handler(val) {
                if (this.value && val) {
                    this.localForm = { ...val };
                }
            },
            deep: true,
        },
    },
    computed: {
        statusOptions() {
            return CarbonCodes.status.getList();
        },
        visibleModel: {
            get() {
                return this.value;
            },
            set(v) {
                this.$emit("input", v);
            },
        },
    },
    methods: {
        debouncedSearchUser() {
            if (this.userSearchDebounceTimer) clearTimeout(this.userSearchDebounceTimer);
            this.userSearchDebounceTimer = setTimeout(() => {
                this.searchUserFromMain();
            }, 300);
        },
        async searchUserFromMain() {
            const q = (this.userSearchQuery || "").trim();
            if (q.length < 2) {
                this.userSearchResults = [];
                return;
            }
            this.userSearchLoading = true;
            try {
                const { data } = await carbonApi.accessControl.searchUsers(q);
                if (data?.success && Array.isArray(data.data?.users)) {
                    this.userSearchResults = data.data.users;
                } else {
                    this.userSearchResults = [];
                }
            } catch (error) {
                console.error("사용자 검색 실패:", error);
                this.userSearchResults = [];
            } finally {
                this.userSearchLoading = false;
            }
        },
        selectSearchUser(user) {
            this.localForm.loginId = user.loginId || "";
            this.localForm.userName = user.userName || "";
            this.localForm.email = user.email || "";
            this.userSearchQuery = "";
            this.userSearchResults = [];
        },
        buildUserPayload(formData, { includeLoginId = false } = {}) {
            const payload = {
                userName: formData.userName?.trim(),
                email: formData.email?.trim(),
                department: formData.department?.trim(),
                status: formData.status,
            };
            if (includeLoginId) {
                payload.loginId = formData.loginId?.trim();
            }
            return Object.entries(payload).reduce((acc, [key, value]) => {
                if (value !== undefined && value !== null && value !== "") {
                    acc[key] = value;
                }
                return acc;
            }, {});
        },
        handleSave() {
            if (!this.localForm.loginId || !this.localForm.userName || !this.localForm.email) {
                this.toastError("로그인 ID, 이름, 이메일은 필수입니다.", "입력 오류");
                return;
            }
            const includeLoginId = this.mode === "create";
            const payload = this.buildUserPayload(this.localForm, { includeLoginId });
            this.$emit("save", { payload, mode: this.mode, form: { ...this.localForm } });
        },
    },
};
</script>

<style scoped>
.modal-form-compact {
    padding: 10px;
}
.form-group-compact {
    margin-bottom: 14px;
}
.form-group-compact label {
    display: block;
    font-size: 12px;
    font-weight: 600;
    color: #475569;
    margin-bottom: 6px;
}
.user-search-section {
    padding-bottom: 12px;
    border-bottom: 1px solid #e2e8f0;
    margin-bottom: 16px;
}
.user-search-section small {
    display: block;
    margin-top: 6px;
    font-size: 11px;
}
.user-search-input-wrapper {
    position: relative;
}
.user-search-spinner {
    position: absolute;
    right: 10px;
    top: 50%;
    transform: translateY(-50%);
    font-size: 12px;
    color: #64748b;
}
.user-search-dropdown {
    position: absolute;
    left: 0;
    right: 0;
    top: 100%;
    margin-top: 2px;
    max-height: 180px;
    overflow-y: auto;
    background: #fff;
    border: 1px solid #cbd5e1;
    border-radius: 4px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    z-index: 100;
}
.user-search-item {
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
    padding: 8px 12px;
    border: none;
    background: none;
    text-align: left;
    font-size: 12px;
    cursor: pointer;
    transition: background 0.15s;
}
.user-search-item:hover {
    background: #f1f5f9;
}
.user-search-id {
    font-weight: 600;
    color: #334155;
    min-width: 100px;
}
.user-search-name {
    color: #64748b;
    flex: 1;
}
.user-search-badge {
    font-size: 10px;
    padding: 2px 6px;
    border-radius: 3px;
}
.user-search-badge.mapped {
    background: #fef3c7;
    color: #92400e;
}
.text-muted {
    color: #94a3b8;
}
.modal-footer-compact {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    padding: 10px;
    border-top: 1px solid #e2e8f0;
}
</style>
